import {
  Box,
  InputAdornment,
  Popover,
  Theme,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ColorizeIcon from '@material-ui/icons/Colorize';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import clsx from 'clsx';
import { FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { WbButton } from '../WbButton';
import { WbTextField } from '../WbTextField';
import { WbTooltip } from '../WbTooltip';

type EyeDropperConstructor = new () => {
  open: (options: { signal: AbortSignal }) => Promise<{ sRGBHex: string }>;
};

export interface WbColorPickerProps {
  label: string;
  value: string;
  onChange: (color: string) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  className?: string;
}

const useStyles = makeStyles(
  (theme: Theme) => ({
    field: {
      '& .MuiOutlinedInput-adornedStart': { paddingLeft: theme.spacing(1) },
      '& .MuiInputAdornment-positionStart': { marginRight: theme.spacing(0.5) },
      '& .MuiInputAdornment-positionEnd': { marginRight: 0 },
      '& input': { minWidth: 0, fontSize: theme.typography.caption.fontSize },
    },
    inputRoot: {
      '&&': { paddingRight: 0 },
    },
    swatch: ({ color }: { color: string }) => ({
      width: theme.spacing(2),
      height: theme.spacing(2),
      flexShrink: 0,
      backgroundColor: color,
      border: `1px solid ${theme.palette.divider}`,
    }),
    popover: {
      padding: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
      width: theme.spacing(30),
      maxWidth: '100%',
      '& .react-colorful': { width: '100%' },
    },
    toggle: {
      '&&': {
        borderLeft: `1px solid ${theme.palette.divider}`,
        borderRadius: 0,
        width: theme.spacing(4),
        minWidth: theme.spacing(4),
        height: theme.spacing(4),
        padding: 0,
        marginRight: 0,
      },
    },
  }),
  { name: 'WbColorPicker' },
);

export const WbColorPicker: FC<WbColorPickerProps> = memo(
  ({
    label,
    value,
    onChange,
    disabled = false,
    error = false,
    helperText,
    className,
  }) => {
    const [draft, setDraft] = useState(value);
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [picking, setPicking] = useState(false);
    const [pickerError, setPickerError] = useState<string>();
    const nativePickerRef = useRef<HTMLInputElement>(null);
    const pickingController = useRef<AbortController>();
    const EyeDropper = (
      window as Window & { EyeDropper?: EyeDropperConstructor }
    ).EyeDropper;
    const valid = /^#[\da-f]{6}$/i.test(draft);
    const classes = useStyles({
      color: /^#[\da-f]{6}$/i.test(value) ? value : 'transparent',
    });

    useEffect(() => setDraft(value), [value]);
    useEffect(() => {
      if (disabled) {
        setAnchor(null);
        pickingController.current?.abort();
        setPicking(false);
      }
    }, [disabled]);
    useEffect(() => () => pickingController.current?.abort(), []);

    const changeColor = useCallback(
      (color: string) => {
        setDraft(color);
        if (/^#[\da-f]{6}$/i.test(color)) onChange(color);
      },
      [onChange],
    );
    const close = useCallback(() => {
      setAnchor(null);
      pickingController.current?.abort();
      setPicking(false);
      setPickerError(undefined);
    }, []);
    const pickFromScreen = useCallback(async () => {
      if (!EyeDropper) {
        nativePickerRef.current?.click();
        return;
      }
      const controller = new AbortController();
      pickingController.current = controller;
      setPicking(true);
      setPickerError(undefined);
      try {
        const { sRGBHex } = await new EyeDropper().open({
          signal: controller.signal,
        });
        if (!controller.signal.aborted) changeColor(sRGBHex);
      } catch (pickError) {
        if (
          !controller.signal.aborted &&
          !(pickError instanceof Error && pickError.name === 'AbortError')
        ) {
          setPickerError('Unable to pick a color from the screen.');
        }
      } finally {
        if (!controller.signal.aborted) setPicking(false);
      }
    }, [EyeDropper, changeColor]);

    return (
      <Box className={clsx(className)}>
        <input
          ref={nativePickerRef}
          type="color"
          hidden
          tabIndex={-1}
          value={valid ? draft : value}
          disabled={disabled}
          aria-label={`Choose ${label.toLowerCase()} color from system picker`}
          onChange={event => changeColor(event.target.value)}
        />
        <WbTextField
          className={classes.field}
          label={label}
          value={draft}
          onChange={event => changeColor(event.target.value)}
          disabled={disabled}
          error={error || !valid}
          helperText={
            !valid ? 'Enter a six-digit HEX color, e.g. #006CA2.' : helperText
          }
          fullWidth
          InputProps={{
            className: classes.inputRoot,
            startAdornment: (
              <InputAdornment position="start">
                <span
                  className={classes.swatch}
                  role="img"
                  aria-label={`${label} color preview`}
                />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <WbTooltip title={`Choose ${label.toLowerCase()} color`}>
                  <WbButton
                    aria-label={`Choose ${label.toLowerCase()} color`}
                    icon={<KeyboardArrowDownIcon />}
                    variant="text"
                    className={classes.toggle}
                    disabled={disabled}
                    aria-haspopup="dialog"
                    aria-expanded={Boolean(anchor)}
                    onClick={event => setAnchor(event.currentTarget)}
                  />
                </WbTooltip>
              </InputAdornment>
            ),
          }}
        />
        <Popover
          open={Boolean(anchor)}
          anchorEl={anchor}
          onClose={close}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <Box
            className={classes.popover}
            role="dialog"
            aria-label={`${label} color picker`}
            onKeyDown={event => {
              if (event.key === 'Escape') {
                event.stopPropagation();
                close();
              }
            }}
          >
            <HexColorPicker
              color={valid ? draft : value}
              onChange={changeColor}
            />
            <WbButton
              icon={<ColorizeIcon />}
              label={
                EyeDropper
                  ? 'Pick color from screen'
                  : 'Choose color from system picker'
              }
              variant="outlined"
              disabled={disabled || picking}
              onClick={pickFromScreen}
            />
            {pickerError && (
              <Typography variant="body2" color="error" role="alert">
                {pickerError}
              </Typography>
            )}
            <WbTextField
              label="HEX"
              value={draft}
              onChange={event => changeColor(event.target.value)}
              error={!valid}
              helperText={!valid ? 'Enter a six-digit HEX color.' : undefined}
              fullWidth
            />
          </Box>
        </Popover>
      </Box>
    );
  },
);
